import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), [href]';

interface DialogFocusOptions {
  isOpen: boolean;
  isBusy?: boolean;
  onClose: () => void;
}

function getFocusableElements(panel: HTMLElement | null): HTMLElement[] {
  return [...(panel?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [])];
}

function keepTabInsidePanel(event: KeyboardEvent, panel: HTMLElement | null) {
  const focusableElements = getFocusableElements(panel);
  if (focusableElements.length === 0) {
    event.preventDefault();
    panel?.focus();
    return;
  }

  const first = focusableElements[0];
  const last = focusableElements[focusableElements.length - 1];
  const wrapPoint = event.shiftKey ? first : last;
  const isInsidePanel = panel?.contains(document.activeElement);
  if (isInsidePanel && document.activeElement !== wrapPoint) return;

  event.preventDefault();
  (event.shiftKey ? last : first).focus();
}

export function useDialogFocus(
  panelRef: RefObject<HTMLElement | null>,
  { isOpen, isBusy = false, onClose }: DialogFocusOptions,
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const isBusyRef = useRef(isBusy);
  isBusyRef.current = isBusy;

  useEffect(() => {
    if (!isOpen) return;

    const focusBeforeOpen = document.activeElement as HTMLElement | null;
    getFocusableElements(panelRef.current)[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isBusyRef.current) onCloseRef.current();
      if (event.key === 'Tab') keepTabInsidePanel(event, panelRef.current);
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      focusBeforeOpen?.focus();
    };
  }, [isOpen, panelRef]);
}
